<template>
  <v-list-item :two-line="!isEditing" :three-line="isEditing" class="my-2">
    <template v-slot:prepend>
      <v-avatar color="grey" class="white--text">{{ getInitials(chitComment.owner!) }}</v-avatar>
    </template>

    <v-list-item-title class="mb-1">
      <span class="font-weight-bold">
        {{ chitComment.owner }}
      </span>
      -
      <v-tooltip location="bottom" open-delay="500">
        <template #activator="{ props }">
          <span v-bind="props" class="subtitle-2 font-weight-regular grey--text text--darken-1">
            {{ timeFromNow(chitComment.updated_at!) }}
            <template v-if="chitComment.isCommentEdited"> (edited) </template>
          </span>
        </template>
        <span>
          {{ format(chitComment.updated_at!, 'MMMM dd, yyyy h:ma') }}
        </span>
      </v-tooltip>
    </v-list-item-title>
    <v-list-item-subtitle class="overflow-auto flex-wrap text-wrap">
      <span v-if="!isEditing" v-html="computedChitComment.text" />
      <v-form v-model="commentValid" ref="form" v-else>
        <v-textarea
          class="mt-2"
          :loading="isUpdatePending"
          dense
          label="Comment"
          placeholder="Edit comment..."
          rows="1"
          auto-grow
          outlined
          hide-details="auto"
          v-model="comment"
          :error-messages="
            updateError
              ? updateError!.fieldErrors['text']
                ? (updateError!.fieldErrors['text'] as string[])
                : updateError!.message
              : ''
          "
          :hint="textHasDifference ? 'updating' : ''"
          @input="clearAllErrors"
        >
          <template #append-inner>
            <div class="v-input__icon v-input__icon--append justify-end">
              <v-icon
                color="primary"
                @click.stop="updateComment()"
                :disabled="!commentValid || isUpdatePending"
                size="22"
              >
                mdi-send
              </v-icon>
            </div>
          </template>
        </v-textarea>
      </v-form>
    </v-list-item-subtitle>

    <template #append v-if="isCommentOwner">
      <div class="d-flex align-end">
        <v-btn
          small
          flat
          icon
          class="mr-1"
          @click.stop="toggleEditingComment()"
          v-if="currentUser?.hasPermission('chits.change_chitcomment')"
        >
          <v-icon size="20"> {{ isEditing ? 'mdi-close' : 'mdi-pencil' }} </v-icon>
        </v-btn>
        <v-btn
          small
          flat
          icon
          @click.stop="openDeleteCommentDialog()"
          v-if="currentUser?.hasPermission('chits.delete_chitcomment')"
        >
          <v-icon size="20" color="error"> mdi-delete </v-icon>
        </v-btn>
      </div>
    </template>
  </v-list-item>

  <ConfirmationDialog
    v-if="computedChitComment"
    width="450"
    title="Delete Chit comment"
    v-model="deleteCommentDialog"
    v-model:error="deleteError"
  >
    <template #message>
      Are you sure you want to delete comment
      <span class="text-medium-high-emphasis font-weight-bold">{{ computedChitComment.text }}</span
      >?
      <br />
      <div class="pt-2">This action cannot be undone.</div>
    </template>

    <template #actions>
      <v-spacer></v-spacer>
      <v-btn
        color="error"
        variant="flat"
        :disabled="isDeletePending"
        :loading="isDeletePending"
        @click="removeComment()"
      >
        Delete
      </v-btn>
    </template>
  </ConfirmationDialog>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import type { VForm } from 'vuetify/components'
import { format } from 'date-fns'

import { cloneDeep } from 'lodash'
import { useAuthStore } from '@/stores'
import { useDeleteChitComment, useUpdateChitComment } from '@/composables/chit'
import { ChitComment, type IChit, type IChitComment } from '@/models/chit'

import { timeFromNow } from '@/utils/date'
import { diffDeep, getInitials } from '@/utils/helpers'

import ConfirmationDialog from '../common/ConfirmationDialog.vue'
import type { ISystemError } from '@/models/error'

interface Props {
  chit: IChit
  chitComment: IChitComment
}

const props = defineProps<Props>()

const authStore = useAuthStore()
const { getUser: currentUser } = storeToRefs(authStore)

const commentValid = ref(false)
const form = ref<VForm | null>(null)

const isCommentOwner = computed(() => {
  return props.chitComment.owner === currentUser.value?.getFullName()
})

const isEditing = ref(false)

const deleteCommentDialog = ref(false)
const computedChitComment = computed(() => props.chitComment as IChitComment)

const comment = ref<string>('')

const textHasDifference = computed(() => comment.value !== computedChitComment.value.text)

const {
  isPending: isUpdatePending,
  error: updateError,
  reset: updateReset,
  mutate: updateChitComment
} = useUpdateChitComment(props.chit.chit_number!)

const {
  isPending: isDeletePending,
  error: deleteError,
  reset: deleteReset,
  mutate: deleteChitComment
} = useDeleteChitComment(props.chit.chit_number!)

function toggleEditingComment() {
  if (!isEditing.value) {
    // To reset the selected chit comment data to the original when rediting
    comment.value = cloneDeep(computedChitComment.value.text)
  }
  isEditing.value = !isEditing.value
}

function clearAllErrors(): void {
  updateReset()
  form.value?.resetValidation()
}

function openDeleteCommentDialog() {
  deleteReset()
  deleteCommentDialog.value = true
}

const localError = ref<ISystemError | null>(null)

const error = computed({
  get: () => {
    return localError.value || updateError.value
  },
  set: (value) => {
    localError.value = value
  }
})

function updateComment() {
  const valid = form.value?.validate()
  if (valid) {
    let chitCommentInstance: IChitComment | null = null

    try {
      chitCommentInstance = new ChitComment({ ...computedChitComment.value, text: comment.value! })
    } catch (e: any) {
      error.value = e
    }

    if (chitCommentInstance) {
      const payload = diffDeep(chitCommentInstance, computedChitComment.value, true)
      updateChitComment(payload, {
        onSuccess: () => {
          isEditing.value = false
        }
      })
    }
  }
}

function removeComment() {
  deleteChitComment(computedChitComment.value!.id!, {
    onSuccess: () => {
      deleteCommentDialog.value = false
    }
  })
}
</script>
<style></style>
